import { BaseHTMLAttributes, FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import Lightbox from 'react-image-lightbox';

import { addTransformToCloudinaryUrl } from '@z-components/utils/url';
import SeeMoreButton from '@z-components/atoms/SeeMoreButton';
import EditButton from '@z-components/atoms/EditButton';
import EmptyModule from '@z-components/molecules/EmptyModule';

import 'react-image-lightbox/style.css';

import styles from './PhotoList.module.scss';

const TRANSLATIONS = {
  en: {
    text: 'photo',
  },
  ja: {
    text: '写真',
  },
};
const PICTURE_SLIDER_SIZE = 1024;
const PICTURE_THUMBNAIL_SIZE = 128;
const PICTURE_MAX_PERCENTAGE = 100;
const DIRECT_BOOKING_IMAGE_PADDING = 16;
interface Props {
  isDashboardView?: boolean;
  isDirectBooking?: boolean;
  language?: 'en' | 'ja';
  name?: string;
  onEditButtonClick?: BaseHTMLAttributes<HTMLButtonElement>['onClick'];
  /** if rowLimit is set, photo margin will be in %, otherwise it will be in px */
  photoMargin?: number;
  photos?: Array<{ caption?: string; createdTime?: number; url: string }>;
  /** max number of photos displayed in a row */
  rowLimit?: number;
  /** position of see more button */
  seeMorePosition?: 'flex-end' | 'center';
  translations: {
    /** Set editLabel to display EditButton */
    editLabel?: string;
    jobType?: string | undefined;
    noCaption: string;
    photos?: string;
    /** Set seeMoreLabel to display SeeMoreButton */
    seeMoreLabel?: string;
  };
}

export const PhotoList: FC<Props> = ({
  isDashboardView = false,
  isDirectBooking,
  onEditButtonClick = () => undefined,
  photos = [],
  photoMargin = 2,
  translations,
  rowLimit,
  seeMorePosition = 'flex-end',
  name,
  language = 'ja',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedPhotos, setDisplayedPhotos] = useState(rowLimit);
  const [isOpen, setIsOpen] = useState(false);
  const photoSlice = photos.slice(0, displayedPhotos);
  const photoSize = useMemo(
    () => (rowLimit ? `${PICTURE_MAX_PERCENTAGE / rowLimit - photoMargin}%` : '120px'),
    [photoMargin, rowLimit]
  );

  const handleSeeMore = useCallback(() => {
    if (!displayedPhotos) {
      return;
    }

    setDisplayedPhotos(displayedPhotos + (rowLimit ?? 0));
  }, [displayedPhotos, rowLimit]);

  const onPhotoClicked = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const onNavigate = (isNext: boolean) => {
    const newIndex = isNext
      ? (currentIndex + 1) % photos.length
      : (currentIndex + photos.length - 1) % photos.length;

    setCurrentIndex(newIndex);
  };

  const cloudinaryUrl = ({ isThumbnail = false, url }: { isThumbnail?: boolean; url: string }) =>
    addTransformToCloudinaryUrl(url, {
      width: isThumbnail ? PICTURE_THUMBNAIL_SIZE : PICTURE_SLIDER_SIZE,
    });

  return (
    <section className="photo-section">
      {translations.photos && (
        <header className={styles.header}>
          <h3 className={styles.title}>
            {name}
            {translations.jobType}
            {translations.photos}
          </h3>
          {translations.editLabel && (
            <EditButton onClick={onEditButtonClick} size={18} label={translations.editLabel} />
          )}
        </header>
      )}

      {photos.length > 0 ? (
        <div
          className={classNames(styles['photo-grid'], {
            [styles['see-more']]: translations.seeMoreLabel && rowLimit,
            [styles['beauty-photo-list']]: isDirectBooking,
          })}
        >
          {photoSlice.map(({ createdTime, url }, index) => {
            const inlinePhotoMargin = `${photoMargin}${rowLimit ? '%' : 'px'}`;
            const backgroundImage = `url(${cloudinaryUrl({ isThumbnail: true, url })})`;
            const photoInlineStyle = {
              backgroundImage,
              marginBottom: inlinePhotoMargin,
              marginRight: inlinePhotoMargin,
              width: isDashboardView ? '72px' : photoSize,
            };
            return (
              <div
                className={classNames(styles.photo, {
                  [styles['client-dashboard-photo']]: isDashboardView,
                })}
                key={`${createdTime ?? ''}.${Math.random()}.${url}`}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => onPhotoClicked(index)}
                role="presentation"
                style={isDirectBooking ? { backgroundImage } : photoInlineStyle}
              />
            );
          })}
        </div>
      ) : (
        <EmptyModule language={language} text={TRANSLATIONS[language].text} />
      )}

      {displayedPhotos &&
        displayedPhotos < photos.length &&
        Boolean(rowLimit) &&
        translations.seeMoreLabel && (
          <div
            className={classNames(styles.bottom, {
              [styles.center]: seeMorePosition === 'center',
            })}
          >
            <img
              className={styles['expand-icon']}
              src="/static/images/icons/Icon_Expand_More.svg"
              alt=""
            />
            <SeeMoreButton onClick={handleSeeMore} label={translations.seeMoreLabel} />
          </div>
        )}

      {photos.length > 0 && isOpen && (
        <Lightbox
          wrapperClassName={classNames(styles.lightbox, {
            [styles['beauty-lightbox']]: isDirectBooking,
            // Lightbox is in a portal so not a child of .theme-beauty. When isDirectBooking is true
            // apply the beauty theme css class to inherit the css vars
            'theme-beauty': isDirectBooking,
          })}
          enableZoom={false}
          imageCaption={photos[currentIndex].caption ?? translations.noCaption}
          imagePadding={isDirectBooking ? DIRECT_BOOKING_IMAGE_PADDING : undefined}
          mainSrc={cloudinaryUrl({ url: photos[currentIndex].url })}
          nextSrc={cloudinaryUrl({ url: photos[(currentIndex + 1) % photos.length].url })}
          prevSrc={cloudinaryUrl({
            url: photos[(currentIndex + photos.length - 1) % photos.length].url,
          })}
          // eslint-disable-next-line react/jsx-no-bind
          onCloseRequest={() => setIsOpen(false)}
          // eslint-disable-next-line react/jsx-no-bind
          onMovePrevRequest={() => onNavigate(false)}
          // eslint-disable-next-line react/jsx-no-bind
          onMoveNextRequest={() => onNavigate(true)}
        />
      )}
    </section>
  );
};

export default PhotoList;
export type { Props as PhotoListProps };
